import { zip as observableZip } from "rxjs";

import type { IComponentController, IScope } from "angular";
import "angular-translate";
import { finalize } from "rxjs/operators";

import type { StateService } from "@uirouter/core";
import { includes } from "lodash";
import type { IAccountRepository, IProfileService } from "App/Modules/Auth";
import type {
    IPartnerSlim,
    IPartnerUser,
    IPartnerUserFlat,
    IPartnerUserSite,
    IPartnerUserSlim,
    IResponseWithLoading,
    ITeamMember,
} from "App/Modules/Models";
import { Permission } from "App/Modules/Models";
import type { IPartnersRepository, IPartnerUsersRepository } from "App/Modules/Repositories";
import type { IModalService, IPageService } from "App/Modules/Services";
import { addLoading } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class PartnerUserDetailsComponentController implements IComponentController {
    public static $inject: string[] = [
        "$scope",
        "$state",
        "mmPartnerUsersRepository",
        "mmPartnersRepository",
        "mmModalService",
        "mmProfileService",
        "mmAccountRepository",
        "irPageService",
    ];

    public partnerId: string;

    public partnerUserId: string;

    public partnerUser: IResponseWithLoading<IPartnerUserFlat>;

    public permissionsRelatedToThisPartner: IResponseWithLoading<Permission[]>;

    public partnerMembershipsForUser: IResponseWithLoading<IPartnerUserSlim[]>;

    public sitesConnectedToUser: IResponseWithLoading<IPartnerUserSite[]>;

    public teamsConnectedToUser: IResponseWithLoading<ITeamMember[]>;

    public createUserAccountResponse: IResponseWithLoading<IPartnerUser>;

    public resetPasswordResponse: IResponseWithLoading<void>;

    public resetTwoFactorAuthResponse: IResponseWithLoading<void>;

    public partner: { data: IPartnerSlim };

    public isCurrentUser = false;

    constructor(
        private readonly $scope: IScope,
        private readonly stateService: StateService,
        private readonly partnerUsersRepository: IPartnerUsersRepository,
        private readonly partnersRepository: IPartnersRepository,
        private readonly modalService: IModalService,
        private readonly profileService: IProfileService,
        private readonly accountRepository: IAccountRepository,
        private readonly pageService: IPageService,
    ) {}

    public $onInit() {
        this.partnerUser = { isLoading: true };

        extendWithLoadingAndFail(this.partnerUsersRepository.getPartnerUser(this.partnerId, this.partnerUserId))
            .pipe(
                addLoading(this.pageService),
                finalize(() => this.$scope.$applyAsync()),
            )
            .subscribe(
                (response: IResponseWithLoading<IPartnerUserFlat>) => {
                    if (!response.hasError && response.data) {
                        this.partnerUser = response;
                        this.isCurrentUser = false;
                        // set partner for breadcrumbs
                        this.partner = {
                            data: response.data.partner,
                        };

                        const profile = this.profileService.getProfileIfAvailable();
                        if (profile && profile.email === this.partnerUser.data.email) {
                            this.isCurrentUser = true;
                        }
                    }
                },
                (error) => {
                    this.partnerUser = error;
                },
            );

        const getPartnerAuthorizedPermissions = extendWithLoadingAndFail(
            this.partnersRepository.getPartnerAuthorizedPermissions(this.partnerId),
        );

        const getPartnersMemberships = extendWithLoadingAndFail(
            this.partnerUsersRepository.getPartnersMemberships(this.partnerId, this.partnerUserId),
        );

        observableZip(getPartnerAuthorizedPermissions, getPartnersMemberships).subscribe(
            ([authorizedPermissions, partnerMemberships]) => {
                this.permissionsRelatedToThisPartner = authorizedPermissions;
                this.partnerMembershipsForUser = partnerMemberships;
            },
            (error) => {
                this.permissionsRelatedToThisPartner = error;
                this.partnerMembershipsForUser = error;
            },
        );

        extendWithLoadingAndFail(this.partnerUsersRepository.getSites(this.partnerId, this.partnerUserId)).subscribe(
            (sites: IResponseWithLoading<IPartnerUserSite[]>) => {
                this.sitesConnectedToUser = sites;
            },
            (error) => {
                this.sitesConnectedToUser = error;
            },
        );

        extendWithLoadingAndFail(
            this.partnerUsersRepository.getTeamMemberships(this.partnerId, this.partnerUserId),
        ).subscribe(
            (teamMemberships) => {
                this.teamsConnectedToUser = teamMemberships;
            },
            (error) => {
                this.teamsConnectedToUser = error;
            },
        );
    }

    public hasLimitedPermissions() {
        return !includes(this.permissionsRelatedToThisPartner.data, Permission.PartnerSettings);
    }

    public editPartnerUser() {
        const editModal = this.modalService.open("irEditPartnerUser", this.partnerUser.data, {
            partnerUser: this.partnerUser.data,
            withLimitedPermissions: this.hasLimitedPermissions(),
        });

        editModal.subscribe(
            (result) => {
                if (result !== null) {
                    Object.assign(this.partnerUser.data, result);
                } else {
                    this.stateService.go("partners.partner", {
                        partnerId: this.partnerId,
                    });
                }
            },
            (reason) => reason,
        );
    }

    public createUserAccount() {
        extendWithLoadingAndFail(
            this.partnerUsersRepository.verify(this.partnerUser.data.partner.id, this.partnerUser.data.id, false),
        )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.createUserAccountResponse = response;
                    if (!response.isLoading) {
                        this.partnerUser.data.user.id = response.data.user.id;
                    }
                },
                (err) => {
                    this.createUserAccountResponse = err;
                },
            );
    }

    public resetPasswordForUser() {
        extendWithLoadingAndFail(this.accountRepository.requestPasswordReset(this.partnerUser.data.email))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.resetPasswordResponse = response;
                },
                (error) => {
                    this.resetPasswordResponse = error;
                },
            );
    }

    public resetTwoFactorAuthForUser() {
        extendWithLoadingAndFail(
            this.accountRepository.requestTwoFactorAuthenticationReset(this.partnerUser.data.email),
        )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.resetTwoFactorAuthResponse = response;
                },
                (error) => {
                    this.resetTwoFactorAuthResponse = error;
                },
            );
    }
}
